import RefreshIcon from '@elementor/icons/RefreshIcon';
import Chip from '@elementor/ui/Chip';
import IconButton from '@elementor/ui/IconButton';
import InputAdornment from '@elementor/ui/InputAdornment';
import InputLabel from '@elementor/ui/InputLabel';
import TextField from '@elementor/ui/TextField';
import { styled } from '@elementor/ui/styles';
import { useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';
import Subheader from '@components/header/subheader';
import { BLOCKS } from '@constants';
import { useScannerWizardContext } from '@context/scanner-wizard-context';
import ArrowRightIcon from '@images/arrow-right-icon';
import {
	StyledSkeleton,
	StyledStatsBlock,
	StyledTitleBox,
} from '@styles/app.styles';
import { __, sprintf } from '@utils/i18n';
import { isValidPageURL } from '@utils/url';

const ScannerHeader = () => {
	const inputId = 'ea11y-scanner-website-input';
	const {
		openedBlock,
		loading,
		isError,
		proxyError,
		pageURL,
		violation: violationsCount,
		rescanPage,
	} = useScannerWizardContext();

	const [inputValue, setInputValue] = useState('');
	const [debouncedValue] = useDebounce(inputValue, 1000);
	const isDirty = Boolean(inputValue) && inputValue !== pageURL;

	useEffect(() => {
		setInputValue(pageURL || '');
	}, [pageURL]);

	const handleChange = (event) => {
		setInputValue(event.target.value);
	};

	const onSubmit = (e) => {
		e.preventDefault();

		if (!isValidPageURL(inputValue)) {
			return;
		}

		if (isDirty) {
			return rescanPage(inputValue);
		}

		rescanPage();
	};

	const showViolationsCountChip =
		!isError && !proxyError && !loading && openedBlock === BLOCKS.main;

	const violationsCountText =
		violationsCount > 0
			? sprintf(__('%s issues found'), violationsCount)
			: __('No issues found');

	if (BLOCKS.main === openedBlock) {
		return (
			<StyledStatsBlock>
				<form onSubmit={onSubmit}>
					<StyledTitleBox>
						<StyledInputLabel htmlFor={inputId}>
							{__('URL scanned')}
						</StyledInputLabel>

						{showViolationsCountChip && (
							<Chip
								size="tiny"
								color={violationsCount > 0 ? 'error' : 'success'}
								variant="outlined"
								label={violationsCountText}
							/>
						)}

						{loading && <StyledSkeleton width={124} height={20} />}
					</StyledTitleBox>

					<StyledTextField
						id={inputId}
						fullWidth
						type="url"
						autoComplete="url"
						variant="outlined"
						size="small"
						color="secondary"
						value={inputValue}
						disabled={loading}
						onChange={handleChange}
						error={Boolean(debouncedValue) && !isValidPageURL(debouncedValue)}
						helperText={
							Boolean(debouncedValue) && !isValidPageURL(debouncedValue)
								? __('Enter a valid URL')
								: ''
						}
						InputProps={{
							endAdornment: (
								<InputAdornment position="end" disabled={loading}>
									<IconButton
										edge="end"
										size="small"
										type="submit"
										sx={{ p: 0.75 }}
										aria-label={
											isDirty ? __('Scan the page') : __('Re-scan the page')
										}
									>
										{isDirty ? (
											<ArrowRightIcon size={14} />
										) : (
											<RefreshIcon fontSize="small" />
										)}
									</IconButton>
								</InputAdornment>
							),
						}}
					/>
				</form>
			</StyledStatsBlock>
		);
	}

	return <Subheader />;
};

const StyledInputLabel = styled(InputLabel)`
	margin-top: ${({ theme }) => theme.spacing(1)};

	color: ${({ theme }) => theme.palette.text.tertiary};
	font-size: 12px;
	line-height: 12px;
	letter-spacing: 0.15px;
`;

const StyledTextField = styled(TextField)`
	.Mui-disabled:disabled {
		-webkit-text-fill-color: ${({ theme }) => theme.palette.text.primary};
		color: ${({ theme }) => theme.palette.text.primary};
	}
`;

export default ScannerHeader;
